//
// DISCLAIMER
//
// Copyright 2019-2026 ArangoDB GmbH, Cologne, Germany
//

import { Dropdown, Header, Popup, Icon, Button, Form } from "semantic-ui-react";
import {
  Model,
  MemoryReserve,
  UpdateDeploymentMemoryReservesRequest as ApiUpdateDeploymentMemoryReservesRequest,
  VolumeInfo as ApiVolumeInfo,
} from "../../api/lib";
import { Field, FieldContent as FC, FieldLabelWide as FL, humanizeDuration, DiskPerformanceInput } from "../../ui/lib";
import { DateTimePopupWithUTCAndLocalTime } from "../../util/dateAndTimeUtils/DateTime";
import { MemoryReserveView } from "./MemoryReserveView";
import { DiskSizeView } from "./DiskSizeView";
import { VolumeInfoView } from "./VolumeInfosViews";
import type { IConfigurationViewArgs } from "./ConfigurationView";

type ConfigurationResourceServersViewArgs = IConfigurationViewArgs & {
  /** When false, hide memory-reserve editors (e.g. Resources tab; shown on Details for sched v3). Default true. */
  showMemoryReserves?: boolean;
  /** When false, hide Memory / CPU / N×size rows (covered by Resources per-role table). Default true. */
  showComputeSummary?: boolean;
};

/** Coordinators / DB servers / disk / agents — shared by Details (v2) and Resources (v3). */
export const ConfigurationResourceServersView = (args: ConfigurationResourceServersViewArgs) => {
  const model = args.model || {};
  const hasAgency = Model.hasAgency(model.model || "");
  const isCluster = Model.isCluster(model.model || "");
  const agentVolumeInfo = args.agentsVolumeInfo || {};
  const dbserverVolumeInfo = args.dbserverVolumeInfo || {};
  const showMemoryReserves = args.showMemoryReserves !== false;
  const showComputeSummary = args.showComputeSummary !== false;

  const onSaveAgentVolumeInfo = (newVolumeType: string, newVolumeIOPS: number, newVolumeThroughput: number) => {
    const newAgentsVolumeInfo: ApiVolumeInfo = {
      type: newVolumeType,
      iops: newVolumeIOPS,
      throughput: newVolumeThroughput,
    };
    return args.onSaveVolumeInfos(newAgentsVolumeInfo, undefined);
  };

  const onSaveDBServerVolumeInfo = (newVolumeType: string, newVolumeIOPS: number, newVolumeThroughput: number) => {
    const newDBServerVolumeInfo: ApiVolumeInfo = {
      type: newVolumeType,
      iops: newVolumeIOPS,
      throughput: newVolumeThroughput,
    };
    return args.onSaveVolumeInfos(undefined, newDBServerVolumeInfo);
  };

  return (
    <div>
      {isCluster && (showComputeSummary || showMemoryReserves) && (
        <>
          <Header size="tiny">Coordinators</Header>
          {showComputeSummary && (
            <Field>
              <FL>Memory</FL>
              <FC>
                <Popup
                  position="right center"
                  trigger={
                    <div>
                      {args.servers ? args.servers.coordinators : "?"}
                      <span> &times; </span>
                      {args.servers ? args.servers.coordinator_memory_size : "?"}
                      <span> GiB Memory</span>
                    </div>
                  }
                  content={
                    <div>
                      {args.memoryResources ? args.memoryResources.coordinator_requests : "?"}
                      <span>/</span>
                      {args.memoryResources ? args.memoryResources.coordinator_limits : "?"}
                      <span> bytes</span>
                    </div>
                  }
                />
              </FC>
            </Field>
          )}
          {showMemoryReserves && (
            <Field>
              <FL>Memory reserve</FL>
              <FC>
                <MemoryReserveView
                  memoryReserve={args.coordinatorsMemoryReserve}
                  canEditMemoryReserves={args.canEditMemoryReserves}
                  onUpdateMemoryReserve={(reserve: MemoryReserve) => {
                    const req: ApiUpdateDeploymentMemoryReservesRequest = { coordinators_memory_reserve: reserve };
                    args.onUpdateMemoryReserve(req);
                  }}
                  createComment={args.createComment}
                />
              </FC>
            </Field>
          )}
          {showComputeSummary && (
            <Field>
              <FL>CPU</FL>
              <FC>
                <div>
                  {args.cpuResources ? args.cpuResources.coordinator_requests : "?"}
                  <span>/</span>
                  {args.cpuResources ? args.cpuResources.coordinator_limits : "?"}
                  <span> vCPU</span>
                </div>
              </FC>
            </Field>
          )}
        </>
      )}

      <Header size="tiny">Database servers</Header>
      <Field>
        <FL>Minimum Servers</FL>
        <FC>{args.servers ? args.servers.minimum_dbservers_count : "-"}</FC>
      </Field>
      {showComputeSummary && (
        <Field>
          <FL>Memory</FL>
          <FC>
            <Popup
              position="right center"
              trigger={
                <div>
                  {args.servers ? args.servers.dbservers : "?"}
                  <span> &times; </span>
                  {args.servers ? args.servers.dbserver_memory_size : "?"}
                  <span> GiB Memory</span>
                </div>
              }
              content={
                <div>
                  {args.memoryResources ? args.memoryResources.dbserver_requests : "?"}
                  <span>/</span>
                  {args.memoryResources ? args.memoryResources.dbserver_limits : "?"}
                  <span> bytes</span>
                </div>
              }
            />
          </FC>
        </Field>
      )}
      {showMemoryReserves && (
        <Field>
          <FL>Memory reserve</FL>
          <FC>
            <MemoryReserveView
              memoryReserve={args.dbserversMemoryReserve}
              canEditMemoryReserves={args.canEditMemoryReserves}
              onUpdateMemoryReserve={(reserve: MemoryReserve) => {
                const req: ApiUpdateDeploymentMemoryReservesRequest = { dbservers_memory_reserve: reserve };
                args.onUpdateMemoryReserve(req);
              }}
              createComment={args.createComment}
            />
          </FC>
        </Field>
      )}
      {showComputeSummary && (
        <Field>
          <FL>CPU</FL>
          <FC>
            <div>
              {args.cpuResources ? args.cpuResources.dbserver_requests : "?"}
              <span>/</span>
              {args.cpuResources ? args.cpuResources.dbserver_limits : "?"}
              <span> vCPU</span>
            </div>
          </FC>
        </Field>
      )}
      <Field>
        <FL>Disk</FL>
        <FC>
          <DiskSizeView
            serverCount={args.servers && args.servers.dbservers}
            diskSize={args.servers && args.servers.dbserver_disk_size}
            diskAutoSizeSettings={args.diskAutoSizeSettings}
            canEditDiskSize={args.canEditDiskSize}
            canEditDiskSizeIgnoreMax={args.canEditDiskSizeIgnoreMax}
            onUpdateDiskSize={args.onUpdateDiskSize}
            createComment={args.createComment}
            lastDiskPerformanceUpdatedAt={args.lastDiskPerformanceUpdatedAt}
            lastDiskSizeUpdatedAt={args.lastDiskSizeUpdatedAt}
            diskRateLimitPeriod={args.diskRateLimitPeriod}
            diskRateLimitActive={args.diskRateLimitActive}
          />
        </FC>
      </Field>
      <Field>
        <FL>Minimum Disk</FL>
        <FC>{args.servers ? (args.servers.minimum_dbserver_disk_size || "0") + " GiB" : "-"}</FC>
      </Field>
      <Field>
        <FL>Disk performance ID</FL>
        <FC>
          {!args.editDiskPerformance && (
            <span>
              {args.diskPerformanceId || "-"}{" "}
              {args.canEditDiskPerformance && <Icon name="pencil" onClick={args.onEditDiskPerformance} className="edit-pencil" />}
            </span>
          )}
          {args.editDiskPerformance && (
            <Form>
              <Form.Field>
                <DiskPerformanceInput
                  {...args}
                  placeholder="disk performance"
                  id={args.diskPerformanceId}
                  onChange={args.onDiskPerformanceChange}
                  notAll
                  regionId={args.regionId}
                  nodeSizeId={model.node_size_id}
                  nodeDiskSize={model.node_disk_size}
                />
                <Button.Group icon basic size="small">
                  <Button icon="cancel" onClick={args.onCancelEditDiskPerformance} />
                </Button.Group>
              </Form.Field>
            </Form>
          )}
        </FC>
      </Field>
      <Field>
        <FL>Disk performance locked</FL>
        <FC>
          <span>{args.diskPerformanceLocked ? "Yes" : "No"}</span>
          {args.canEditDiskPerformanceLocked && (
            <Dropdown icon="pencil" inline className="icon tiny edit-pencil">
              <Dropdown.Menu>
                {args.diskPerformanceLocked && (
                  <Dropdown.Item
                    key="remove-dpl-n"
                    text="Turn off disk performance locked for the deployment (do not update VolumeInfo)"
                    onClick={() => args.onUpdateDiskPerformanceLocked(false, false)}
                  />
                )}
                {args.diskPerformanceLocked && (
                  <Dropdown.Item
                    key="remove-dpl-y"
                    text="Turn off disk performance locked for the deployment (use regular VolumeInfo)"
                    onClick={() => args.onUpdateDiskPerformanceLocked(false, true)}
                  />
                )}
                {!args.diskPerformanceLocked && (
                  <Dropdown.Item
                    key="set-pl"
                    text="Turn on disk performance locked  for the deployment"
                    onClick={() => args.onUpdateDiskPerformanceLocked(true, false)}
                  />
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </FC>
      </Field>
      <Field>
        <FL>Disk performance last updated</FL>
        <FC>
          {args.lastDiskPerformanceUpdatedAt ? <DateTimePopupWithUTCAndLocalTime dateTime={args.lastDiskPerformanceUpdatedAt} label="Last updated at" /> : "-"}
        </FC>
      </Field>
      <Field>
        <FL>Disk size last updated</FL>
        <FC>{args.lastDiskSizeUpdatedAt ? <DateTimePopupWithUTCAndLocalTime dateTime={args.lastDiskSizeUpdatedAt} label="Last updated at" /> : "-"}</FC>
      </Field>
      <Field>
        <FL>Disk Rate limit</FL>
        <FC>
          {args.diskRateLimitPeriod ? humanizeDuration(args.diskRateLimitPeriod) : "-"}
          {args.diskRateLimitActive && "(active-for=" + humanizeDuration(args.diskRateLimitActive) + ")"}
        </FC>
      </Field>
      <Field>
        <FL>Disk params</FL>
        <FC>
          <VolumeInfoView
            title="DBServer"
            volumeType={dbserverVolumeInfo.type}
            volumeIOPS={dbserverVolumeInfo.iops}
            volumeThroughput={dbserverVolumeInfo.throughput}
            canEdit={args.canEditVolumeInfos}
            onEdit={args.onEditDBServersVolumeInfos}
            editing={args.editDBServersVolumeInfos}
            onCancelEdit={args.onCancelEditDBServersVolumeInfos}
            onSaveEdit={onSaveDBServerVolumeInfo}
            processingUpdate={args.processingUpdateVolumeInfos}
            createComment={args.createComment}
            lastDiskPerformanceUpdatedAt={args.lastDiskPerformanceUpdatedAt}
            lastDiskSizeUpdatedAt={args.lastDiskSizeUpdatedAt}
            diskRateLimitPeriod={args.diskRateLimitPeriod}
            diskRateLimitActive={args.diskRateLimitActive}
          />
        </FC>
      </Field>

      {hasAgency && (
        <>
          <Header size="tiny">Agents</Header>
          {showComputeSummary && (
            <Field>
              <FL>Memory</FL>
              <FC>
                <Popup
                  position="right center"
                  trigger={
                    <div>
                      {args.size ? args.size.agents : "?"}
                      <span> &times; </span>
                      {args.size ? args.size.agent_memory_size : "?"}
                      <span> GiB Memory</span>
                    </div>
                  }
                  content={
                    <div>
                      {args.memoryResources ? args.memoryResources.agent_requests : "?"}
                      <span>/</span>
                      {args.memoryResources ? args.memoryResources.agent_limits : "?"}
                      <span> bytes</span>
                    </div>
                  }
                />
              </FC>
            </Field>
          )}
          {showMemoryReserves && (
            <Field>
              <FL>Memory reserve</FL>
              <FC>
                <MemoryReserveView
                  memoryReserve={args.agentsMemoryReserve}
                  canEditMemoryReserves={args.canEditMemoryReserves}
                  onUpdateMemoryReserve={(reserve: MemoryReserve) => {
                    const req: ApiUpdateDeploymentMemoryReservesRequest = { agents_memory_reserve: reserve };
                    args.onUpdateMemoryReserve(req);
                  }}
                  createComment={args.createComment}
                />
              </FC>
            </Field>
          )}
          {showComputeSummary && (
            <Field>
              <FL>CPU</FL>
              <FC>
                <div>
                  {args.cpuResources ? args.cpuResources.agent_requests : "?"}
                  <span>/</span>
                  {args.cpuResources ? args.cpuResources.agent_limits : "?"}
                  <span> vCPU</span>
                </div>
              </FC>
            </Field>
          )}
          <Field>
            <FL>Disk</FL>
            <FC>
              <div>
                {args.size ? args.size.agents : "?"}
                <span> &times; </span>
                {args.size ? args.size.agent_disk_size : "?"}
                <span> GiB Disk</span>
              </div>
            </FC>
          </Field>
          <Field>
            <FL>Disk params</FL>
            <FC>
              <VolumeInfoView
                title="Agent"
                volumeType={agentVolumeInfo.type}
                volumeIOPS={agentVolumeInfo.iops}
                volumeThroughput={agentVolumeInfo.throughput}
                canEdit={args.canEditVolumeInfos}
                onEdit={args.onEditAgentsVolumeInfos}
                editing={args.editAgentsVolumeInfos}
                onCancelEdit={args.onCancelEditAgentsVolumeInfos}
                onSaveEdit={onSaveAgentVolumeInfo}
                processingUpdate={args.processingUpdateVolumeInfos}
                createComment={args.createComment}
                lastDiskPerformanceUpdatedAt={args.lastDiskPerformanceUpdatedAt}
                lastDiskSizeUpdatedAt={args.lastDiskSizeUpdatedAt}
                diskRateLimitPeriod={args.diskRateLimitPeriod}
                diskRateLimitActive={args.diskRateLimitActive}
              />
            </FC>
          </Field>
        </>
      )}
    </div>
  );
};
